/**
 * Arcℏimedian DSaaS — App Router
 * Maps URL paths to page components with auth guards.
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AppLayout } from './components/AppLayout';
import { LoadingScreen } from './components/LoadingScreen';
import { CookieBanner } from './components/CookieBanner';
import { ErrorBoundary } from './components/ErrorBoundary';

function P({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary inline>{children}</ErrorBoundary>;
}

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DatasetsPage = lazy(() => import('./pages/DatasetsPage'));
const ModelsPage = lazy(() => import('./pages/ModelsPage'));
const ExperimentsPage = lazy(() => import('./pages/ExperimentsPage'));
const PipelinesPage = lazy(() => import('./pages/PipelinesPage'));
const AnalysesPage = lazy(() => import('./pages/AnalysesPage'));
const AnalysisDetailPage = lazy(() => import('./pages/AnalysisDetailPage'));
const CheckEmailPage = lazy(() => import('./pages/CheckEmailPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TutorialPage = lazy(() => import('./pages/TutorialPage'));
const DataTypesPage = lazy(() => import('./pages/DataTypesPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));

// ── Route Guards ──
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <LandingPage />;
  return <AppLayout />;
}

export function App() {
  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Legal pages — always public, no auth needed */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Auth pages */}
          <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/check-email" element={<ProtectedRoute><CheckEmailPage /></ProtectedRoute>} />

          {/* Root: landing for guests, app for authenticated users */}
          <Route path="/" element={<RootRoute />}>
            <Route index element={<P><DashboardPage /></P>} />
            <Route path="datasets" element={<P><DatasetsPage /></P>} />
            <Route path="models" element={<P><ModelsPage /></P>} />
            <Route path="experiments" element={<P><ExperimentsPage /></P>} />
            <Route path="pipelines" element={<P><PipelinesPage /></P>} />
            <Route path="analyses" element={<P><AnalysesPage /></P>} />
            <Route path="analyses/compare" element={<P><ComparePage /></P>} />
            <Route path="analyses/:id" element={<P><AnalysisDetailPage /></P>} />
            <Route path="welcome" element={<WelcomePage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="settings" element={<P><SettingsPage /></P>} />
            <Route path="tutorial" element={<P><TutorialPage /></P>} />
            <Route path="data-types" element={<P><DataTypesPage /></P>} />
          <Route path="admin" element={<P><AdminPage /></P>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>

      {/* Cookie consent banner — shown on all pages until consent is recorded */}
      <CookieBanner />
    </>
  );
}
